import {
  Card,
  Divider,
  Header,
  PrimaryButton,
  Space,
  Text,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import { VendorLogoBadge } from '../VendorLogoBadge'
import { ConversationOrderSummaryItem } from './ConversationOrderSummaryItem'
import messages from './messages'
import { ConversationOrderSummaryCardStyled } from './styles/ConversationOrderSummaryCardStyled'
import { ConversationOrderSummaryDividerStyled } from './styles/ConversationOrderSummaryDividerStyled'
import {
  type ConversationCartAction,
  type ConversationVendor,
} from '../../value-objects/ConversationTurnItem'

export interface IConversationOrderSummaryItem {
  id: string
  name: string
  img?: string
  quantity: number
  price: number
}

export interface IConversationOrderSummaryProps {
  items: IConversationOrderSummaryItem[]
  total: number
  currency: CurrencyName
  disabled?: boolean
  isContinueLoading?: boolean
  isRemoveDisabled?: boolean
  vendor?: ConversationVendor
  totalLabel?: string
  continueAction?: ConversationCartAction
  disclaimer?: string
  onQuantityChange: (itemId: string, quantity: number) => void
  onRemove: (itemId: string) => void
  onContinue: () => void
}

export const ConversationOrderSummary = ({
  items,
  total,
  currency,
  disabled,
  isContinueLoading,
  isRemoveDisabled,
  vendor,
  totalLabel,
  continueAction,
  disclaimer,
  onQuantityChange,
  onRemove,
  onContinue,
}: IConversationOrderSummaryProps) => {
  const intl = useProcurifyIntl()

  return (
    <ConversationOrderSummaryCardStyled>
      <Card.Header>
        <Space direction='horizontal' size='small' align='center'>
          <Header as='h5'>
            {intl.formatMessage(messages.heading, { count: items.length })}
          </Header>
          {vendor && <VendorLogoBadge vendor={vendor} />}
        </Space>
      </Card.Header>

      <Card.Content>
        <Space direction='vertical' size='small'>
          {items.map((item) => (
            <Space.Item key={item.id} minWidth={0}>
              <ConversationOrderSummaryItem
                item={item}
                currency={currency}
                disabled={disabled}
                isRemoveDisabled={isRemoveDisabled}
                onQuantityChange={onQuantityChange}
                onRemove={onRemove}
              />
            </Space.Item>
          ))}
        </Space>
      </Card.Content>

      <ConversationOrderSummaryDividerStyled>
        <Divider space={0} />
      </ConversationOrderSummaryDividerStyled>

      <Card.Footer>
        <Space direction='vertical' size='medium'>
          <Space direction='horizontal' justify='space-between' align='center'>
            <Text inline bold>
              {totalLabel ?? intl.formatMessage(messages.estimatedTotal)}
            </Text>
            <span className='fs-mask'>
              <Price inline price={total} currency={currency} />
            </span>
          </Space>

          <PrimaryButton
            analyticsId='conversation-continue-to-request-button'
            showIconOnEnd
            icon={continueAction?.icon ?? 'pointer-right'}
            disabled={disabled}
            isLoading={isContinueLoading}
            onClick={onContinue}
          >
            {continueAction?.text ??
              intl.formatMessage(messages.continueButton)}
          </PrimaryButton>

          <Text color='textSecondary'>
            {disclaimer ?? intl.formatMessage(messages.disclaimer)}
          </Text>
        </Space>
      </Card.Footer>
    </ConversationOrderSummaryCardStyled>
  )
}
